/**
 * ClientScriptManager
 *
 * Flavor-independent owner of the reserved `__client_tools__` partner bundle.
 *
 * The backend pushes a second, flavor-independent `partner_bundle` whose
 * `partner_id` is the sentinel `__client_tools__`. It carries per-tool client
 * scripts (each an optional `pre_code_js`, a required `code_js` main step, and
 * an optional `post_code_js`) plus auto-run library scripts. Unlike the
 * ecommerce flavor's partner bundle, this one must run regardless of which
 * flavor (if any) is active, on BOTH the voice (`VoiceSDK_v2`) and text-chat
 * (`TextChatSDK`) channels.
 *
 * This manager owns a single dedicated {@link PartnerScriptRunner} instance —
 * constructed with `{ genericCtx: true }` so client-tool scripts get a
 * platform-agnostic ctx (args / host / fetch / log / emit / runAdapter, no
 * ecommerce-only platform / normalizeCart / refreshUI). It reuses the runner
 * class as-is; it does not fork it.
 *
 * Coexistence: the ecommerce flavor keeps its own runner and its own
 * `partner_bundle` handling for every other partner id. Each SDK routes by
 * `partnerId` — `__client_tools__` → this manager, anything else → the existing
 * flavor path. Bundles are therefore kept separate per partner id.
 *
 * Wire contract (unchanged):
 *   bundle  : { t:'partner_bundle', partner_id:'__client_tools__',
 *               adapters:{ tool:{ code_js, pre_code_js?, post_code_js?, ... } },
 *               autoRun:{ name:{ code_js } } }
 *   invoke  : { t:'run_partner_script', requestId, partnerId:'__client_tools__', action, args }
 *   result  : { t:'run_partner_script_result', requestId, partnerId, action, ok, result|error }
 */
import { PartnerScriptRunner } from '../ecommerce/PartnerScriptRunner.js';

/** Reserved partner id for the flavor-independent client-tool bundle. */
export const CLIENT_TOOLS_PARTNER_ID = '__client_tools__';

/** Mirror of ClientToolsRegistry.MAX_RESULT_SIZE — 50KB cap on result payloads. */
const MAX_RESULT_SIZE = 50_000;

const LOG = '[ClientScriptManager]';

export class ClientScriptManager {
  /**
   * @param {object} opts
   * @param {(msg: object) => void} opts.send  Send a JSON frame back to the backend
   *   (VoiceSDK.sendMessage / TextChatSDK.sendRaw). Used for both run_partner_script_result
   *   envelopes and adapter ctx.emit() pushes.
   * @param {string} [opts.label]  Short tag for logs (e.g. 'voice' / 'text').
   */
  constructor({ send, label = '' } = {}) {
    this._send = typeof send === 'function' ? send : () => {};
    this._label = label;
    this.runner = new PartnerScriptRunner({ genericCtx: true });
    // Unsolicited adapter pushes (ctx.emit) go back over the same channel.
    this.runner.setEmitCallback((msg) => this._send(msg));
  }

  /**
   * True when this message is a `partner_bundle` addressed to the sentinel
   * `__client_tools__` partner id. Both SDKs use this to route the bundle here
   * instead of (or in addition to) the flavor path.
   * @param {object} message
   * @returns {boolean}
   */
  static isClientToolsBundle(message) {
    return !!message
      && (message.t === 'partner_bundle' || message.type === 'partner_bundle')
      && message.partner_id === CLIENT_TOOLS_PARTNER_ID;
  }

  /**
   * True when this run_partner_script envelope targets the sentinel partner id.
   * @param {object} message
   * @returns {boolean}
   */
  static isClientToolsScript(message) {
    return !!message && message.partnerId === CLIENT_TOOLS_PARTNER_ID;
  }

  /**
   * True when this `client_tool_call` envelope is actually a partner-script
   * dispatch for the sentinel partner id. The backend's sync path
   * (PartnerScriptDispatcher.executeAdapter → executeClientToolAwait) sends
   * scripts as `client_tool_call` with `toolName:'run_partner_script'` and
   * `parameters.partner_id` — normally answered by the ecommerce flavor's
   * registered 'run_partner_script' tool handler. With no flavor loaded there
   * is no such handler, so both SDKs use this predicate to route the call here
   * BEFORE the ClientToolsRegistry lookup (which would NO_HANDLER it).
   * @param {object} message  a client_tool_call message
   * @returns {boolean}
   */
  static isClientToolsToolCall(message) {
    return !!message
      && message.toolName === 'run_partner_script'
      && !!message.parameters
      && message.parameters.partner_id === CLIENT_TOOLS_PARTNER_ID;
  }

  /**
   * Execute a `client_tool_call` partner-script dispatch (see
   * {@link isClientToolsToolCall}) against the client-tool runner and answer
   * with the standard `client_tool_result` / `client_tool_error` envelopes —
   * the exact shapes ClientToolsRegistry sends, since the backend awaits the
   * reply keyed by `toolCallId`. Enforces the 50KB result cap.
   *
   * @param {object} message  { toolCallId, toolName, parameters:{ action, args, partner_id } }
   * @param {string|null} conversationId  the SDK's current conversation id
   */
  async handleClientToolCall(message, conversationId = null) {
    const { toolCallId, toolName } = message || {};
    const { action, args } = (message && message.parameters) || {};
    const startedAt = Date.now();

    console.log(`${LOG}${this._tag()} client_tool_call dispatch action=${action} toolCallId=${toolCallId}`);

    const sendError = (error, errorCode) => {
      this._send({
        t: 'client_tool_error',
        toolCallId,
        toolName,
        conversationId,
        error,
        errorCode,
        executionTimeMs: Date.now() - startedAt,
      });
    };

    if (!action) {
      sendError('run_partner_script missing action', 'EXECUTION_ERROR');
      return;
    }

    try {
      const raw = await this.runner.runAdapter(action, args || {});
      // A void script (side-effect only, no return) is a success — undefined
      // would be dropped from the JSON envelope and confuse the backend parse.
      const result = raw === undefined ? { ok: true } : raw;

      let serializedSize = 0;
      try {
        serializedSize = JSON.stringify(result).length;
      } catch {
        serializedSize = 0;
      }
      if (serializedSize > MAX_RESULT_SIZE) {
        const sizeKB = Math.round(serializedSize / 1024);
        const limitKB = Math.round(MAX_RESULT_SIZE / 1024);
        console.error(`${LOG}${this._tag()} result for '${action}' exceeds size limit (${sizeKB}KB > ${limitKB}KB)`);
        sendError(`Result too large: ${sizeKB}KB exceeds ${limitKB}KB limit`, 'RESULT_TOO_LARGE');
        return;
      }

      console.log(`${LOG}${this._tag()} client_tool_call done action=${action} toolCallId=${toolCallId} (${Date.now() - startedAt}ms)`);
      this._send({
        t: 'client_tool_result',
        toolCallId,
        toolName,
        conversationId,
        result,
        executionTimeMs: Date.now() - startedAt,
      });
    } catch (err) {
      console.error(`${LOG}${this._tag()} client_tool_call threw action=${action} toolCallId=${toolCallId}:`, err);
      sendError((err && err.message) || 'Unknown error', 'EXECUTION_ERROR');
    }
  }

  /**
   * Handle a `partner_bundle` for `__client_tools__`. Mirrors
   * EcommerceFlavor's bundle handling: merge autoRun entries into the adapters
   * map (so ctx.runAdapter can reach them), load the bundle (replacing any
   * previous one for this partner — reconnect case), then execute each autoRun
   * entry exactly once.
   * @param {object} data  the partner_bundle message
   */
  setBundle(data) {
    // emit callback is wired in the constructor and survives bundle reloads;
    // re-assert it here so it is always fresh for the active SDK instance.
    this.runner.setEmitCallback((msg) => this._send(msg));

    const merged = {
      ...data,
      adapters: { ...(data?.adapters || {}), ...(data?.autoRun || {}) },
    };
    this.runner.setBundle(merged);

    for (const action of Object.keys(data?.autoRun || {})) {
      this.runner.runAdapter(action, {})
        .then((r) => console.log(`${LOG}${this._tag()} auto-run '${action}':`, r))
        .catch((e) => console.warn(`${LOG}${this._tag()} auto-run '${action}' failed:`, e));
    }
  }

  /**
   * Execute a `run_partner_script` envelope against the client-tool runner and
   * post the `run_partner_script_result` back over the channel. Enforces the
   * 50KB result-size cap (oversize → ok:false, error:'RESULT_TOO_LARGE').
   *
   * An unknown action (e.g. bundle not yet received) resolves to ok:false with
   * the runner's `adapter_not_in_bundle` reason — same behavior as the existing
   * path.
   *
   * @param {object} message  { requestId, partnerId, action, args }
   */
  async handleRunPartnerScript(message) {
    const { requestId, partnerId, action } = message || {};
    const args = (message && message.args) || {};

    if (!requestId) {
      console.warn(`${LOG}${this._tag()} run_partner_script missing requestId; dropping`, message);
      return;
    }

    const startedAt = Date.now();
    console.log(`${LOG}${this._tag()} run_partner_script dispatch partner=${partnerId} action=${action} requestId=${requestId}`);

    try {
      const result = await this.runner.runAdapter(action, args);
      const elapsedMs = Date.now() - startedAt;
      const ok = result && typeof result === 'object' && 'ok' in result ? !!result.ok : true;

      // 50KB result-size cap (mirror ClientToolsRegistry.MAX_RESULT_SIZE).
      let serializedSize = 0;
      try {
        serializedSize = JSON.stringify(result).length;
      } catch {
        serializedSize = 0;
      }
      if (serializedSize > MAX_RESULT_SIZE) {
        const sizeKB = Math.round(serializedSize / 1024);
        const limitKB = Math.round(MAX_RESULT_SIZE / 1024);
        console.error(`${LOG}${this._tag()} result for '${action}' exceeds size limit (${sizeKB}KB > ${limitKB}KB)`);
        this._send({
          t: 'run_partner_script_result',
          requestId,
          partnerId,
          action,
          ok: false,
          error: 'RESULT_TOO_LARGE',
        });
        return;
      }

      if (ok) {
        console.log(`${LOG}${this._tag()} run_partner_script ok partner=${partnerId} action=${action} requestId=${requestId} (${elapsedMs}ms)`);
      } else {
        const reason = (result && (result.reason || result.error)) || 'unknown';
        console.warn(`${LOG}${this._tag()} run_partner_script failed partner=${partnerId} action=${action} requestId=${requestId} reason=${reason} (${elapsedMs}ms)`, result);
      }

      this._send({
        t: 'run_partner_script_result',
        requestId,
        partnerId,
        action,
        ok,
        result,
      });
    } catch (err) {
      const elapsedMs = Date.now() - startedAt;
      console.error(`${LOG}${this._tag()} run_partner_script threw partner=${partnerId} action=${action} requestId=${requestId} (${elapsedMs}ms):`, err);
      this._send({
        t: 'run_partner_script_result',
        requestId,
        partnerId,
        action,
        ok: false,
        error: (err && err.message) || String(err),
      });
    }
  }

  _tag() {
    return this._label ? ` [${this._label}]` : '';
  }
}
